/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  CloudArrowUpIcon as CloudUpIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/solid';
import * as React from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CollectionNode } from '@/services/types/collection-types';

import { useGetBlobsQuery } from '../../../services/api/blobService';
import { useGetCollectionTreeQuery } from '../../../services/api/collectionService';
import {
  createAutoSelectionMessage,
  findMatchingDriversLicense,
} from '../../treatment/v3/request/package-edit/utils/documentMatching';

interface DocumentSelectionModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  onSelectDocuments: (blobIds: string[]) => Promise<void>;
  initialSelectedBlobIds?: string[];
  singleFile?: boolean;
  onUpload?: (
    files: File[],
    collectionId: string,
  ) => Promise<string[] | void>;
  autoSelectPattern?: {
    clientFirstName?: string;
    clientLastName?: string;
  };
  onAutoSelect?: (blobId: string, reason: string) => void;
  /** Optional description text displayed below the modal title. */
  description?: string;
  /** When true, the Save Selection button is disabled until at least one file is selected. */
  requireSelection?: boolean;
  /** Optional bypass callback. When provided, an "Already Received by Firm" button is shown. */
  onBypass?: (reason: string) => Promise<void>;
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  id: string;
  isRemoving?: boolean;
}

export const DocumentSelectionModal: React.FC<DocumentSelectionModalProps> = ({
  open,
  onClose,
  matterId,
  onSelectDocuments,
  initialSelectedBlobIds,
  singleFile,
  onUpload,
  autoSelectPattern,
  onAutoSelect,
  description,
  requireSelection,
  onBypass,
}) => {
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });
  const { data: blobs, refetch: refetchBlobs } = useGetBlobsQuery(matterId, {
    skip: !matterId,
  });
  const [selectedCollectionId, setSelectedCollectionId] =
    React.useState<string>('');
  const [selectedBlobIds, setSelectedBlobIds] = React.useState<Set<string>>(
    new Set(initialSelectedBlobIds ?? []),
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = React.useState<string>(
    onUpload ? 'upload' : 'select',
  );
  const [autoSelectedBlobId, setAutoSelectedBlobId] = React.useState<
    string | null
  >(null);
  const [autoSelectMessage, setAutoSelectMessage] = React.useState<
    string | null
  >(null);
  const [isBypassing, setIsBypassing] = React.useState(false);
  const [bypassReasonOpen, setBypassReasonOpen] = React.useState(false);
  const [bypassReason, setBypassReason] = React.useState('');

  // Set root collection as default when data is loaded
  React.useEffect(() => {
    if (collectionTree && !selectedCollectionId) {
      setSelectedCollectionId(collectionTree.id);
    }
  }, [collectionTree, selectedCollectionId]);

  // Sync initialSelectedBlobIds when the modal opens (e.g., drag-dropped files)
  React.useEffect(() => {
    if (open && initialSelectedBlobIds && initialSelectedBlobIds.length > 0) {
      setSelectedBlobIds(new Set(initialSelectedBlobIds));
    }
  }, [open, initialSelectedBlobIds]);

  // Auto-selection logic
  React.useEffect(() => {
    // Only auto-select if:
    // 1. We have blobs loaded
    // 2. We have an auto-select pattern
    // 3. We're in single file mode
    // 4. There's no initial selection
    // 5. We haven't already auto-selected
    if (
      !blobs ||
      !autoSelectPattern ||
      !singleFile ||
      initialSelectedBlobIds?.length ||
      autoSelectedBlobId
    ) {
      return;
    }

    const { clientFirstName, clientLastName } = autoSelectPattern;

    // Use utility function to find matching driver's license
    const clientNames = {
      firstName: clientFirstName,
      lastName: clientLastName,
    };
    const match = findMatchingDriversLicense(blobs, clientNames);

    if (!match) return;

    // Auto-select the best match
    setSelectedBlobIds(new Set([match.blob.id]));
    setAutoSelectedBlobId(match.blob.id);

    // Create user-friendly message
    const reason = createAutoSelectionMessage(
      match.blob.name,
      match.matchReason,
    );
    setAutoSelectMessage(reason);

    // Notify parent component
    onAutoSelect?.(match.blob.id, reason);
  }, [
    blobs,
    autoSelectPattern,
    singleFile,
    initialSelectedBlobIds,
    autoSelectedBlobId,
    onAutoSelect,
  ]);

  // Filter blobs based on selected collection
  const filteredBlobs = React.useMemo(() => {
    if (!blobs) return [];

    return blobs.filter(
      (blob) =>
        (selectedCollectionId === collectionTree?.id ||
          blob.collection_details.id === selectedCollectionId) &&
        (searchQuery === '' ||
          blob.name.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [blobs, selectedCollectionId, collectionTree, searchQuery]);

  // Get selected blob objects for the transfer list
  const selectedBlobs = React.useMemo(() => {
    if (!blobs) return [];
    return blobs.filter((blob) => selectedBlobIds.has(blob.id));
  }, [blobs, selectedBlobIds]);

  const handleToggleSelect = (blobId: string) => {
    const newSelected = new Set(selectedBlobIds);
    if (newSelected.has(blobId)) {
      newSelected.delete(blobId);
    } else {
      newSelected.add(blobId);
    }
    if (singleFile) {
      setSelectedBlobIds(new Set([blobId]));
    } else {
      setSelectedBlobIds(newSelected);
    }
  };

  const renderCollectionOptions = (
    node: CollectionNode,
    level = 0,
  ): JSX.Element[] => {
    const options: JSX.Element[] = [];
    const prefix = '\u00A0'.repeat(level * 2);

    options.push(
      <SelectItem
        key={node.id}
        value={node.id}
        className="cursor-pointer hover:bg-gray-100"
      >
        {prefix + (node.name === 'root' ? 'All Files' : node.name)}
      </SelectItem>,
    );

    if (node.children) {
      node.children.forEach((child: CollectionNode) => {
        options.push(...renderCollectionOptions(child, level + 1));
      });
    }

    return options;
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFiles = async (files: File[]) => {
    if (!onUpload || files.length === 0) return;

    // Add files to the upload queue with pending status
    const newUploadedFiles: UploadedFile[] = files.map((file) => ({
      file,
      status: 'pending' as const,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);

    // Upload all files in parallel
    const uploadPromises = newUploadedFiles.map(async (uploadedFile) => {
      try {
        // Update status to uploading
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'uploading' } : f,
          ),
        );

        const blobIds = await onUpload(
          [uploadedFile.file],
          selectedCollectionId,
        );

        // Update status to success
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'success' } : f,
          ),
        );

        // Refetch blobs after successful upload
        await refetchBlobs();

        // After successful upload and refetch, auto-select the blob and remove from queue
        setTimeout(async () => {
          // Small delay to ensure blobs are refreshed
          const { data: refreshedBlobs } = await refetchBlobs();

          // Auto-select: prefer blob ID from upload response, fall back to name matching
          const blobId = blobIds?.[0];
          if (blobId) {
            if (singleFile) {
              setSelectedBlobIds(new Set([blobId]));
            } else {
              setSelectedBlobIds(
                (prev) => new Set([...prev, blobId]),
              );
            }
          } else if (refreshedBlobs) {
            const uploadedBlob = refreshedBlobs.find(
              (blob) => blob.name === uploadedFile.file.name,
            );
            if (uploadedBlob) {
              if (singleFile) {
                setSelectedBlobIds(new Set([uploadedBlob.id]));
              } else {
                setSelectedBlobIds(
                  (prev) => new Set([...prev, uploadedBlob.id]),
                );
              }
            }
          }

          // Always remove from upload queue after success
          setTimeout(() => {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === uploadedFile.id
                  ? { ...f, isRemoving: true }
                  : f,
              ),
            );

            // Actually remove after animation completes
            setTimeout(() => {
              setUploadedFiles((prev) =>
                prev.filter((f) => f.id !== uploadedFile.id),
              );
            }, 300);
          }, 1000); // Show success state before animating out
        }, 500);
      } catch (error) {
        // Update status to error
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === uploadedFile.id ? { ...f, status: 'error' } : f,
          ),
        );
        console.error('Upload failed:', error);
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      await handleUploadFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await handleUploadFiles(droppedFiles);
  };

  const removeUploadedFile = (id: string) => {
    // Mark as removing for animation
    setUploadedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isRemoving: true } : f)),
    );

    // Actually remove after animation
    setTimeout(() => {
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    }, 300);
  };

  const handleSubmit = async () => {
    try {
      await onSelectDocuments(Array.from(selectedBlobIds));
      onClose();
    } catch {
      // Error is handled by the caller (e.g. toast in handleTrrAttachmentSelect).
      // Keep the modal open so the user can retry without re-selecting files.
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes > 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending':
        return <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />;
      case 'uploading':
        return (
          <CloudArrowUpIcon className="h-5 w-5 text-primary animate-pulse" />
        );
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      default:
        return <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="bg-white max-w-2xl w-full flex flex-col z-99999 rounded-md border border-zinc-200 shadow-xl p-0 gap-0 max-h-[85vh]"
        overlayClassName="z-[99998] bg-black/60"
      >
        {/* ── Fixed header ── */}
        <AlertDialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-100 flex-none">
          <AlertDialogTitle className="text-sm font-bold text-zinc-900 tracking-tight">
            Attach Evidence
          </AlertDialogTitle>
          {description && (
            <p className="text-xs font-medium text-zinc-500 mt-0.5">{description}</p>
          )}
        </AlertDialogHeader>

        {/* ── Scrollable body — never collapses the list ── */}
        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3 flex flex-col gap-3">

          {/* Tab toggle */}
          {onUpload && (
            <div className="flex rounded-md border border-zinc-200 bg-zinc-100 p-0.5 gap-0.5">
              <button
                type="button"
                onClick={() => setActiveTab('upload')}
                className={`flex-1 h-7 text-xs font-semibold rounded transition-colors ${
                  activeTab === 'upload'
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                Upload New
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('select')}
                className={`flex-1 h-7 text-xs font-semibold rounded transition-colors ${
                  activeTab === 'select'
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                Select Existing
              </button>
            </div>
          )}

          {/* Destination folder — always visible */}
          <div>
            <Label className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1.5 block">
              Destination Folder
            </Label>
            <Select
              value={selectedCollectionId}
              onValueChange={(value) => setSelectedCollectionId(value)}
            >
              <SelectTrigger className="w-full h-9 text-xs font-medium border-zinc-200">
                <SelectValue placeholder="Select a folder" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-zinc-200 z-[99999]">
                {collectionTree && renderCollectionOptions(collectionTree)}
              </SelectContent>
            </Select>
          </div>

          {/* ── Upload tab ── */}
          {activeTab === 'upload' && onUpload && (
            <>
              {uploadedFiles.length > 0 && (
                <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                  <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                    Upload Queue
                  </h3>
                  <div className="border border-zinc-200 rounded-md p-2 max-h-32 overflow-y-auto bg-zinc-50">
                    <div className="space-y-1.5">
                      {uploadedFiles.map((uploadedFile) => (
                        <div
                          key={uploadedFile.id}
                          className={`flex items-center justify-between p-2 bg-white rounded border transition-all duration-300 ease-out ${
                            uploadedFile.isRemoving
                              ? 'scale-95 opacity-0 translate-x-4'
                              : 'scale-100 opacity-100 translate-x-0 animate-in slide-in-from-left-2 fade-in duration-300'
                          } ${
                            uploadedFile.status === 'success'
                              ? 'border-green-200 bg-green-50'
                              : uploadedFile.status === 'error'
                                ? 'border-red-200 bg-red-50'
                                : uploadedFile.status === 'uploading'
                                  ? 'border-primary/30 bg-primary/5'
                                  : 'border-zinc-200'
                          }`}
                        >
                          <div className="flex items-center min-w-0 flex-1">
                            <div>{getStatusIcon(uploadedFile.status)}</div>
                            <div className="ml-2 min-w-0 flex-1">
                              <p className="text-xs font-medium truncate text-zinc-900">
                                {uploadedFile.file.name}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {formatFileSize(uploadedFile.file.size)}
                              </p>
                            </div>
                          </div>
                          {uploadedFile.status !== 'uploading' && (
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(uploadedFile.id)}
                              className="ml-2 text-zinc-400 hover:text-red-500 transition-colors"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div
                className={`border-2 border-dashed rounded-md transition-all duration-200 cursor-pointer ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-zinc-300 hover:border-zinc-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleFileSelect}
              >
                <div className="flex flex-col items-center justify-center p-8">
                  <CloudUpIcon
                    className={`h-10 w-10 mb-3 transition-colors ${
                      isDragging ? 'text-primary' : 'text-zinc-300'
                    }`}
                  />
                  <p className="text-sm font-bold text-zinc-700 mb-1">
                    {isDragging ? 'Drop files here' : 'Drag & drop files here'}
                  </p>
                  <p className="text-xs font-medium text-zinc-500 mb-3">
                    or browse to upload
                  </p>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileSelect();
                    }}
                    className="bg-zinc-900 text-white hover:bg-zinc-800 text-xs font-bold h-8 px-4"
                  >
                    <CloudArrowUpIcon className="h-3.5 w-3.5 mr-1.5" />
                    Browse Files
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    multiple
                    className="hidden"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Select tab ── */}
          {activeTab === 'select' && (
            <>
              {autoSelectMessage && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start justify-between animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-start gap-2">
                    <svg
                      className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-blue-900">
                        Document auto-selected
                      </p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        {autoSelectMessage}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBlobIds(new Set());
                      setAutoSelectedBlobId(null);
                      setAutoSelectMessage(null);
                    }}
                    className="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-xs"
              />

              {/* Document list — fixed height, always visible regardless of selection */}
              <div className="h-52 border border-zinc-200 rounded-md overflow-y-auto">
                {filteredBlobs.length > 0 ? (
                  filteredBlobs.map((blob) => (
                    <div
                      key={blob.id}
                      className={`flex items-center px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer transition-colors ${
                        autoSelectedBlobId === blob.id
                          ? 'bg-blue-50 border-l-2 border-l-blue-500'
                          : ''
                      }`}
                      onClick={() => handleToggleSelect(blob.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleSelect(blob.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Select ${blob.name}`}
                    >
                      <Checkbox
                        className="mr-3 cursor-pointer data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        checked={selectedBlobIds.has(blob.id)}
                        onCheckedChange={() => handleToggleSelect(blob.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <DocumentIcon className="h-4 w-4 text-zinc-400 mr-2.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-xs text-zinc-900 truncate">
                            {blob.name}
                          </span>
                          {autoSelectedBlobId === blob.id && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                              Auto
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {formatFileSize(blob.file_size)} •{' '}
                          {blob.file_extension?.toUpperCase().replace('.', '')}{' '}
                          • {new Date(blob.modified_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-6 text-zinc-400">
                    <FolderIcon className="h-8 w-8 mb-2" />
                    <p className="text-xs font-medium text-zinc-600">
                      No documents found
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Try a different folder or search term
                    </p>
                  </div>
                )}
              </div>

              <p className="text-xs font-medium text-zinc-500">
                {selectedBlobIds.size} document
                {selectedBlobIds.size !== 1 ? 's' : ''} selected
              </p>
            </>
          )}
        </div>

        {/* ── Fixed footer — selected chips + actions ── */}
        <div className="flex-none border-t border-zinc-100 px-5 pt-3 pb-4">
          {selectedBlobs.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-zinc-700">
                  Selected Documents ({selectedBlobs.length})
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedBlobIds(new Set())}
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              {/* Chips — capped at 2 rows, then scroll */}
              <div className="flex flex-wrap gap-1.5 max-h-14 overflow-y-auto">
                {selectedBlobs.map((blob) => (
                  <div
                    key={blob.id}
                    className="flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs border border-primary/20 hover:bg-primary/20 transition-colors"
                  >
                    <DocumentIcon className="h-3 w-3 flex-shrink-0" />
                    <span className="max-w-xs truncate font-medium">
                      {blob.name}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleSelect(blob.id);
                      }}
                      className="ml-0.5 text-primary/60 hover:text-red-500 transition-colors"
                      aria-label={`Remove ${blob.name}`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            {/* Left: bypass */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {onBypass && !bypassReasonOpen && (
                <button
                  type="button"
                  onClick={() => setBypassReasonOpen(true)}
                  disabled={isBypassing}
                  className="text-xs font-semibold text-zinc-500 hover:text-zinc-700 underline underline-offset-2 transition-colors whitespace-nowrap"
                >
                  No docs needed
                </button>
              )}
              {onBypass && bypassReasonOpen && (
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <input
                    type="text"
                    value={bypassReason}
                    onChange={(e) => setBypassReason(e.target.value)}
                    placeholder="Why are docs not needed?"
                    className="flex-1 min-w-0 h-8 px-2.5 text-xs font-medium text-zinc-900 border border-zinc-300 rounded bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent placeholder:text-zinc-400"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && bypassReason.trim()) {
                        setIsBypassing(true);
                        onBypass(bypassReason.trim()).finally(() =>
                          setIsBypassing(false),
                        );
                      }
                      if (e.key === 'Escape') {
                        setBypassReasonOpen(false);
                        setBypassReason('');
                      }
                    }}
                  />
                  <Button
                    onClick={async () => {
                      if (!bypassReason.trim()) return;
                      setIsBypassing(true);
                      try {
                        await onBypass(bypassReason.trim());
                      } finally {
                        setIsBypassing(false);
                      }
                    }}
                    disabled={isBypassing || !bypassReason.trim()}
                    className="h-8 px-3 text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800"
                  >
                    {isBypassing ? '...' : 'Skip'}
                  </Button>
                </div>
              )}
            </div>

            {/* Right: cancel + submit */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  onClose();
                  setSelectedBlobIds(new Set(initialSelectedBlobIds ?? []));
                  setUploadedFiles([]);
                  setActiveTab(onUpload ? 'upload' : 'select');
                  setBypassReasonOpen(false);
                  setBypassReason('');
                }}
                variant="outline"
                disabled={isBypassing}
                className="h-8 text-xs font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="h-8 px-4 text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800"
                disabled={
                  isBypassing ||
                  (requireSelection && selectedBlobIds.size === 0)
                }
              >
                Attach{selectedBlobIds.size > 0 ? ` (${selectedBlobIds.size})` : ''}
              </Button>
            </div>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
